import axios from 'axios'; // The Axios library is used to transform object data to JSON Data
import queryString from 'query-string';
import appConfig from '../../config/app_config'; // The AppConfig is used to retrieve general info from the web
import store from '../store'; // The store library is used to retrieve dynamic stored data
import responseFromDss from './DssResponse'; // The dssResponse library is used to check the received data for success of error
import NetworkConnectionManager from './NetworkConnectionManager';
import UserObject from '../objects/UserObject.js';

const MAX_REQUESTS_COUNT = 4;
const INTERVAL_MS = 10;
let PENDING_REQUESTS = 0;

/**
 * @brief adds default dssArguments to request.
 *
 * @param args DssArguments that will be adjusted
 * @returns DssArguments
 */
function createDssArguments(args) {
  const argsUpdate = { ...args };

  let userLanguageId = 14;
  const userLanguage = store.getters['auth/userLanguage'];
  if (userLanguage != null && userLanguage.LanguageId != null) {
    userLanguageId = userLanguage.LanguageId;
  }

  // add UserId to DssArguments
  if (argsUpdate.UserId == null || argsUpdate.UserId.trim() === '') {
    argsUpdate.UserId = store.getters['auth/userName'];
  }

  argsUpdate.TempUserId = store.getters['auth/tmpUserId'];
  argsUpdate.LanguageTagId = userLanguageId;
  argsUpdate.AccountId = store.getters['Account/partnerGroupDetails'].AccountId;
  argsUpdate.ApiVersion = 6;
  argsUpdate.Url = store.getters.getPartnerGroupUrl;
  argsUpdate.Origin = 'Web';

  if(store.getters.isProfUser){
    argsUpdate.Client = appConfig.profClientMap;
  } else {
    argsUpdate.Client = appConfig.clientMap;
  }

  const webHostName = window.location.hostname;
  const webProtocol = window.location.protocol;
  argsUpdate.WebDomain = `${webProtocol}//${webHostName}`;

  return argsUpdate;
}

/**
 * @param requestConfig config for axios requests
 * @returns instance of axios with interceptors
 */
function createAxiosInstance(requestConfig) {
  const Instance = axios.create(requestConfig);

  // Axios Request Interceptor
  Instance.interceptors.request.use(
    (config) =>
      new Promise((resolve, reject) => {
        const interval = setInterval(() => {
          if (PENDING_REQUESTS < MAX_REQUESTS_COUNT) {
            PENDING_REQUESTS += 1;
            clearInterval(interval);
            resolve(config);
          }
        }, INTERVAL_MS);
      }),
    (error) => Promise.reject(error)
  );

  // Axios Response Interceptor
  Instance.interceptors.response.use(
    (response) => {
      PENDING_REQUESTS = Math.max(0, PENDING_REQUESTS - 1);
      return Promise.resolve(response);
    },
    (error) => {
      PENDING_REQUESTS = Math.max(0, PENDING_REQUESTS - 1);
      return Promise.reject(error);
    }
  );

  return Instance;
}

function onResponse(
  payload,
  showError,
  response,
  successCallback,
  errorCallback
) {
  const dssResponse = new responseFromDss(response.data);

  if (dssResponse.success) {
    console.groupCollapsed(
      `%cThe call [${payload.Request}] on [${payload.ObjectId}] was successful`,
      'background-color: #81ad61; color: white; border-radius: 25px; padding: 0 5px'
    );
    console.log('Response: ', dssResponse);
    console.trace();
    console.groupEnd();
    successCallback(dssResponse);
    store.commit('setNetworkError', false);
  } else {
    if (dssResponse.data.ErrorCode === 'E_TOK_001') {
      console.log(`%cThe call [${payload.Request}] on [${payload.ObjectId}] was not successful! The AccessToken is expired. User is logged out.`,
        'background-color: #f3584d; color: white; border-radius: 25px; padding: 0 5px')
      UserObject.logout();
    } else {
      console.groupCollapsed(
        `%cThe call [${payload.Request}] on [${payload.ObjectId}] was not successful`,
        'background-color: #f3584d; color: white; border-radius: 25px; padding: 0 5px'
      );
      console.log('Response: ', dssResponse);
      console.trace();
      console.groupEnd();
    }
    errorCallback(dssResponse);
  }
}

function onError(error, errorCallback) {
  if (error.response) {
    // Request made and server responded
    console.log(
      'ERROR! Request was received, but server responded with error:'
    );
    console.log(error.response.data);
    console.log(error.response.status);
    console.log(error.response.headers);
  } else if (error.request) {
    // The request was made but no response was received
    console.log('The request was made but no response was received, request:');
    console.log(error.request);
    if (
      !store.getters.getUserWantsToQuitOverlay &&
      !store.getters.getNetworkError
    ) {
      store.commit('setNetworkError', true);
      console.log('network error set in dssrequest');
    }
  } else {
    // Something happened in setting up the request that triggered an Error
    console.log(
      'Something happened in setting up the request that triggered an Error: ',
      error.message
    );
  }
  errorCallback(error);
}

/**
 * @brief This function is a wrapper for Axios.
 * It handles default parameters in post and get requests.
 * It also handles default events at success and errors.
 *
 * @param successCallback the callback when function returns success
 * @param errorCallback the callback when function returns error
 * @param action the functionId for the dbLibFunction
 * @param objectId the backend objectId to use as server
 * @param args the payload for the db function
 * @param timeout timer for handling the function, default is 60000 milliseconds (1 minute)
 * @param showError a boolean flag that returns the error dialog from Linda
 */
function doRequest(
  successCallback,
  errorCallback,
  action,
  objectId,
  args,
  requestMethod = 'POST',
  timeout = 60 * 1000,
  showError = true,
  callType = 'SYNC'
) {
  const DssArguments = createDssArguments(args);

  const payload = {
    DssArguments,
    ObjectId: objectId,
    Request: action,
    CallType: callType,
  };

  const url = `https://${appConfig.dss.domain}:${appConfig.dss.port}/`;

  const requestConfig = {
    method: requestMethod,
    url,
    timeout,
    headers: {
      Authorization: `Bearer ${store.getters['auth/token']}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    data: payload,
  };

  if (requestMethod.toUpperCase() === 'GET') {
    // [GET] request, add query params to request
    requestConfig.params = {
      json: JSON.stringify(payload),
    };

    requestConfig.paramsSerializer = function serializeParams(params) {
      return queryString.stringify(params);
    };
  }

  console.log(`Going to do a request: ${action}, url: ${url}`)

  // Check server state
  NetworkConnectionManager.checkServerState();

  const api = createAxiosInstance(requestConfig);

  if (requestMethod.toUpperCase() === 'GET') {
    // [GET] request
    return api
      .get(url, requestConfig)
      .then((response) => {
        onResponse(
          payload,
          showError,
          response,
          successCallback,
          errorCallback
        );
      })
      .catch((error) => {
        onError(error, errorCallback);
      });
  }

  // [POST] request
  return api
    .post(url, payload, requestConfig)
    .then((response) => {
      onResponse(payload, showError, response, successCallback, errorCallback);
    })
    .catch((error) => {
      onError(error, errorCallback);
    });
}

function addLog(
  userId,
  objectId,
  objectType,
  screen,
  position,
  popUpDelay = 5
) {
  const logArray = [];

  const log = {
    UserId: userId,
    UnixTime: new Date().getTime() / 1000,
    ObjectId: objectId,
    ObjectType: objectType,
    Screen: screen,
    Position: position,
    Platform: 'WEB',
  };

  logArray[0] = log;

  const payload = {
    Log: logArray,
  };

  const object = store.getters['auth/userName']
    ? store.getters['auth/userName']
    : store.getters['auth/tmpUserId'];

  doRequest(
    () => {
      setTimeout(() => {
        getPopup();
      }, popUpDelay * 1000);
    },
    () => {},
    'addLog',
    object,
    payload,
    'POST',
    60 * 1000,
    true,
    'DROP'
  );
}

function getPopup() {
  const payload = {};

  const object = store.getters['auth/userName']
    ? store.getters['auth/userName']
    : store.getters['auth/tmpUserId'];

  doRequest(
    (response) => {
      if (response.data.GrowthVitamin != null) {
        store.commit('addGrowthVitamin', response.data.GrowthVitamin);
        window.EventBus.fire(
          'activateGrowthDialog',
          response.data.GrowthVitamin
        );
      }
    },
    () => {},
    'getPopUp',
    object,
    payload
  );
}


/**
 * @brief This function is used to upload a file in the format of FormData
 * @param {FormData} formData file data
 */
 async function uploadFile(formData) {
  // Check server state
  NetworkConnectionManager.checkServerState();

  const url = `https://${appConfig.dss.domain}:${appConfig.dss.port}/file_upload`;

  const requestConfig = {
    url,
    headers: {
      Authorization: `Bearer ${store.getters['auth/token']}`,
      'Content-Type': 'multipart/form-data',
    },
  };

  const api = createAxiosInstance(requestConfig);

  try {
    const response = await api.post(url, formData, requestConfig);

    console.groupCollapsed('%cFile is uploaded successfully',
      'background-color: #81ad61; color: white; border-radius: 25px; padding: 0 5px');
    console.log('Response: ', response);
    console.trace();
    console.groupEnd();

    return response.data;
  } catch (error) {
    console.groupCollapsed('%cFailed to upload the file',
      'background-color: #f3584d; color: white; border-radius: 25px; padding: 0 5px');
    console.log('Error: ', error);
    console.trace();
    console.groupEnd();

    throw error;
  }
}

export { doRequest as default, addLog, uploadFile };
